import { type IApplicationContext } from 'shared/types/applicationContext';

type TEnsureAdfoxApiAvailableParams = {
  applicationContext: IApplicationContext;
};

type TLogger = IApplicationContext['logger'];

let loadPromise: Promise<void> | null = null;

export function ensureAdfoxApiAvailable({
  applicationContext: { logger, config },
}: TEnsureAdfoxApiAvailableParams): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  if (window.Ya && window.Ya.adfoxCode) {
    loadPromise = Promise.resolve();

    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    /**
     * yaContextCb — официальный ready-сигнал yandex'а: после инициализации context.js
     * yandex сам вызовет все коллбэки из массива. Используем его, чтобы не ловить race
     * между script.onload и появлением window.Ya.adfoxCode (и чтобы корректно отработать
     * случай, когда тег скрипта уже есть в DOM и load мог отстрелить до подписки).
     */
    window.yaContextCb ??= [];
    window.yaContextCb.push(() => resolve());

    const adfoxScriptUrl = config.getStrict<string>('MarTech.AdBanner.AdfoxScriptUrl');

    const existingScript = window.document.querySelector<HTMLScriptElement>(`script[src="${adfoxScriptUrl}"]`);

    if (existingScript) {
      existingScript.addEventListener('error', () => handleLoadError(reject, logger));

      return;
    }

    const script = window.document.createElement('script');
    script.src = adfoxScriptUrl;
    script.async = true;
    script.addEventListener('error', () => handleLoadError(reject, logger));

    window.document.body.appendChild(script);
  }).catch(error => {
    /**
     * Сбрасываем singleton при ошибке, чтобы следующий маунт баннера смог повторить попытку
     * (например, после временного сбоя сети или отключения adBlock).
     */
    loadPromise = null;

    throw error;
  });

  return loadPromise;
}

function handleLoadError(reject: (reason: Error) => void, logger: TLogger): void {
  logger.error("Ошибка при загрузке скрипта 'context.js', возможно включён adBlock", {
    domain: 'src/shared/utils/adfox/ensureAdfoxApiAvailable.ts',
  });

  reject(new Error('context.js script load error'));
}
